import { SvgIcon } from '@progress/kendo-react-common'
import { mapMarkerIcon, dollarIcon, userIcon, clockIcon } from '@progress/kendo-svg-icons'
import { useState, useRef, PropsWithChildren } from 'react'
import { fetchApi } from 'services/api'
import { HoverCard } from './HoverCard'
import dayjs from 'dayjs'

type CustomerSalesperson = {
  Slot: number;
  FullName: string;
  Territory: string;
}

type CustomerInfo = {
  CustomerID: number;
  CustomerName: string;
  CustomerNumber: string;
  Active: boolean;
  AddressLine1: string;
  AddressLine2: string;
  City: string;
  State: string;
  ZipCode: string;
  TimeZone: string;
  LocalIanaTimeZoneID: string;
  IndustryVertical: string;
  AccountStatus: string;
  SmartbidEnabled: boolean;
  OnboardingDateTime: string | null;
  MostRecentTradeDate: string | null;
  Salespeople: CustomerSalesperson[];
  OnCreditHold: boolean;
  CreditLimit: number;
  CreditAvailable: number;
}

type CustomerHoverCardProps = {
  customerId: number;
};

const fetchCustomerData = async (customerId: number): Promise<CustomerInfo | null> => {
  return await fetchApi(`/api/Customer/CustomerHoverCard/${customerId}`)
    .catch((): null => {
      return null;
    });
}

// Executive-level money rounding: $0 · $48K · $2.4M
const formatMoney = (value: number | null | undefined): string => {
  if (value == null) return '—'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`
  return `${sign}$${Math.round(abs)}`
}

const formatMonthYear = (value: string | null): string | null => {
  if (!value) return null
  return dayjs(value).format('MMM YYYY')
}

const formatRelative = (value: string | null): string | null => {
  if (!value) return null
  const d = dayjs(value)
  const days = dayjs().startOf('day').diff(d.startOf('day'), 'day')
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return d.format('MMM YYYY')
}

// Current local time at the customer, e.g. "2:32 PM CST"
const formatLocalTime = (iana: string | null | undefined, abbreviation: string | null | undefined): string | null => {
  if (!iana) return null
  const time = dayjs().tz(iana).format('h:mm A')
  return abbreviation ? `${time} ${abbreviation}` : time
}

export const CustomerHoverCard: React.FC<PropsWithChildren<CustomerHoverCardProps>> = (props) =>
  <CustomerHoverCardContent key={props.customerId} {...props} />

const CustomerHoverCardContent: React.FC<PropsWithChildren<CustomerHoverCardProps>> = ({ customerId, children }) => {
  const [customer, setCustomer] = useState<CustomerInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const request = useRef(0)

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const load = async () => {
    if (customer || isLoading) return
    const currentRequest = ++request.current
    setIsLoading(true)
    try {
      const data = await fetchCustomerData(customerId)
      if (request.current === currentRequest) setCustomer(data)
    } finally {
      if (request.current === currentRequest) setIsLoading(false)
    }
  }

  const cityState = customer ? [customer.City, customer.State].filter(Boolean).join(', ') : ''
  const cityLine = customer ? [cityState, customer.ZipCode].filter(Boolean).join(' ') : ''
  const onboarded = formatMonthYear(customer?.OnboardingDateTime ?? null)
  const lastTrade = formatRelative(customer?.MostRecentTradeDate ?? null)
  const subtitle = customer ? [`#${customer.CustomerNumber}`, customer.IndustryVertical].filter(Boolean).join(' · ') : ''
  const localTime = customer ? formatLocalTime(customer.LocalIanaTimeZoneID, customer.TimeZone) : null

  return <HoverCard key={customerId} label="Customer details" width={340} onOpen={load} content={<>
              {isLoading && !customer ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: '120px' }}>
                  <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading...</span>
                </div>
              ) : customer ? (
                <>
                  <div className="d-flex align-items-start mb-3">
                    <div className="position-relative mr-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '64px',
                          height: '64px',
                          backgroundColor: '#d1ecde',
                          border: '2px solid rgba(100, 149, 237, 0.2)',
                          fontSize: 22,
                          fontWeight: '600',
                          color: '#1b5e3c'
                        }}
                      >
                        {getInitials(customer.CustomerName)}
                      </div>
                    </div>

                    <div className="flex-grow-1">
                      <h6 className="card-title mb-1 font-weight-bold">
                        {customer.CustomerName}
                      </h6>
                      {subtitle && <p className="card-text mb-1 k-text-subtle small">
                        {subtitle}
                      </p>}
                      <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                        {customer.AccountStatus && customer.AccountStatus !== 'Existing' && (
                          <span className="badge badge-pill px-2 py-1 small text-secondary" style={{
                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                            border: '1px solid rgba(158, 158, 158, 0.2)'
                          }}>
                            {customer.AccountStatus}
                          </span>
                        )}
                        {!customer.Active && (
                          <span className="badge badge-pill px-2 py-1 small text-secondary" style={{
                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                            border: '1px solid rgba(158, 158, 158, 0.2)'
                          }}>
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {(customer.AddressLine1 || cityLine) && <div className="d-flex align-items-start mb-2">
                    <SvgIcon themeColor='primary' icon={mapMarkerIcon} className='mr-2 mt-1' />
                    <span className="small k-text-subtle">
                      {customer.AddressLine1 && <>{customer.AddressLine1}<br /></>}
                      {cityLine}
                    </span>
                  </div>}

                  {localTime && <div className="d-flex align-items-center mb-2">
                    <SvgIcon themeColor='primary' icon={clockIcon} className='mr-2' />
                    <span className="small k-text-subtle">{localTime}</span>
                  </div>}

                  {customer.Salespeople.length > 0 && <div className="mb-2">
                    {customer.Salespeople.map(sp => (
                      <div key={sp.Slot} className="d-flex align-items-center mb-1">
                        <SvgIcon themeColor='primary' icon={userIcon} className='mr-2' />
                        <span className="small k-text-subtle">
                          {sp.FullName}{sp.Territory ? ` · ${sp.Territory}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>}

                  <div className="d-flex align-items-center mb-2">
                    <SvgIcon themeColor='primary' icon={dollarIcon} className='mr-2' />
                    <span className={`small ${customer.OnCreditHold ? 'text-danger font-weight-medium' : 'k-text-subtle'}`}>
                      {formatMoney(customer.CreditAvailable)} available of {formatMoney(customer.CreditLimit)} limit{customer.OnCreditHold && ' · On credit hold'}
                    </span>
                  </div>

                  {(onboarded || lastTrade) && (
                    <div className="small k-text-subtle pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                      {onboarded && <>Onboarded {onboarded}</>}
                      {onboarded && lastTrade && ' · '}
                      {lastTrade && <>Last trade {lastTrade}</>}
                    </div>
                  )}
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: '120px' }}>
                  <span className="k-text-subtle">Customer not found</span>
                </div>
              )}
  </>}>{children}</HoverCard>
}
